/* eslint-disable no-nested-ternary */
import React, { useState, useEffect, useMemo } from 'react';
import { Grid2 } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import moment from 'moment';
import { BillTypeEnum } from './BillType.enum';
import ExpenseDatePicker from './ExpenseDatePicker';
import { ExpenseStatusValuesEnum } from './ExpenseStatus.enum';
import { COLORS } from '../../../utils/Application_Constants';
import eventBus from './EventBus';
import { ExpenseModeEnum } from './ExpenseMode.enum';
import { ExpenseCategoryEnum } from './ExpenseCategory.enum';
import { ExpenseTypeEnum } from './ExpenseType.enum';

const ExpenseDateRange = (props) => {
    const {
        index,
        expense,
        mode,
        isPosted,
        isPendingSubmission,
        isViewOnly,
        isInvoiceReviewLocked = false,
        enableEditByCorrection = false,
    } = props;
    const { control, errors, watch, setValue } = useFormContext();
    const billType = watch(`expenseEntries[${index}].billType`);
    const isCreditCard = mode === ExpenseModeEnum.CreditCard;
    const requiresBillType = !isCreditCard && !billType;
    const isClinicianDeduction = billType === 'ClinicianDeduction';
    const [required, setRequired] = useState({ required: true });
    const nonBillable =
        billType &&
        (billType === BillTypeEnum.NonBillTeam || billType === BillTypeEnum.NonBillProducer);
    const startDate = watch(`expenseEntries[${index}].startDate`);
    const endDate = watch(`expenseEntries[${index}].endDate`);
    const minStartDate = useMemo(() => {
        if (
            mode === ExpenseModeEnum.CreditCard ||
            mode === ExpenseModeEnum.ClinicianReimbursement
        ) {
            return moment(new Date()).startOf('day').subtract(2, 'years');
        }
        if (nonBillable) {
            return moment(new Date()).startOf('day').subtract(1, 'year');
        }
        return moment(new Date()).startOf('day').subtract(6, 'months');
    }, [mode, nonBillable]);
    const twoMonthsOut = moment(new Date()).startOf('day').add(60, 'days');
    const maxStartDate = useMemo(() => {
        if (
            mode === ExpenseModeEnum.CreditCard ||
            mode === ExpenseModeEnum.ClinicianReimbursement
        ) {
            return moment(new Date()).startOf('day').add(1, 'year');
        }
        if (endDate) {
            return moment(endDate).isSameOrBefore(twoMonthsOut) ? moment(endDate) : twoMonthsOut;
        }
        return twoMonthsOut;
    }, [endDate, mode]);
    const minEndDate = useMemo(() => {
        if (startDate) {
            return moment(startDate);
        }
        if (
            mode === ExpenseModeEnum.CreditCard ||
            mode === ExpenseModeEnum.ClinicianReimbursement
        ) {
            return moment(new Date()).startOf('day').subtract(2, 'years');
        }
        return moment(new Date()).startOf('day').subtract(6, 'months');
    }, [startDate, mode]);
    const maxEndDate = useMemo(() => {
        if (
            mode === ExpenseModeEnum.CreditCard ||
            mode === ExpenseModeEnum.ClinicianReimbursement
        ) {
            return moment(new Date()).startOf('day').add(1, 'year');
        }
        return moment(new Date()).startOf('day').add(6, 'months');
    }, [mode]);

    const handleDateChange = (e, field) => {
        if (e && e.startOf('day')) {
            setValue(`expenseEntries[${index}].${field}`, e.startOf('day').format('YYYY-MM-DD'));
            if (field === 'startDate') {
                eventBus.emit(`startDateChanged${index}`, {
                    data: e.startOf('day').format('YYYY-MM-DD'),
                });
            }
        } else {
            setValue(`expenseEntries[${index}].${field}`, e);
            if (field === 'startDate') {
                eventBus.emit(`startDateChanged${index}`, { data: e });
            }
        }
    };

    useEffect(() => {
        const aDate = expense.startDate
            ? moment(expense.startDate).startOf('day').format('YYYY-MM-DD')
            : null;
        eventBus.emit(`startDateChanged${index}`, { data: aDate });
        return () => {
            // Unmounted from view
            eventBus.emit(`startDateChanged${index}`, { data: null }); // Call function when component is removed
        };
    }, []);

    useEffect(() => {
        if (mode === ExpenseModeEnum.CreditCard) {
            setRequired({
                required:
                    expense?.category === ExpenseCategoryEnum.Travel &&
                    ![ExpenseTypeEnum.BaggageFee, ExpenseTypeEnum.Fuel].includes(expense?.type) &&
                    !isClinicianDeduction &&
                    (expense?.status === ExpenseStatusValuesEnum.Approved ||
                        expense?.status === ExpenseStatusValuesEnum.InvoiceReview),
            });
            return;
        }

        setRequired({
            required:
                expense?.category === ExpenseCategoryEnum.Travel &&
                ![ExpenseTypeEnum.BaggageFee, ExpenseTypeEnum.Fuel].includes(expense?.type),
        });
    }, [expense?.status, expense?.category, expense?.type]);

    return (
        <Grid2 item size={{ xs: 12 }}>
            <Grid2 container spacing={3}>
                <Grid2 item size={{ xs: 12, md: 6 }}>
                    <Controller
                        control={control}
                        name={`expenseEntries[${index}].startDate`}
                        defaultValue={expense.startDate || null}
                        rules={
                            isPosted
                                ? {}
                                : {
                                      required: required.required
                                          ? (requiresBillType && 'Bill type is required') ||
                                            'Start date required'
                                          : false,
                                      validate: {
                                          invalid: (value) =>
                                              !value || moment(value).isValid() || 'Invalid date',
                                          maxDate: (value) => {
                                              if (!value) return true;
                                              if (endDate && moment(value).isAfter(endDate)) {
                                                  return 'Start date must be before end date';
                                              }
                                              if (moment(value).isAfter(maxStartDate)) {
                                                  return `Start date must be within ${maxStartDate.diff(
                                                      moment().startOf('day'),
                                                      'months'
                                                  )} months`;
                                              }
                                              return true;
                                          },
                                          minDate: (value) =>
                                              !minStartDate ||
                                              !value ||
                                              moment(value).isSameOrAfter(minStartDate) ||
                                              'Min date reached',
                                      },
                                  }
                        }
                        render={({ onChange, value }) => (
                            <ExpenseDatePicker
                                onChange={(e) => {
                                    onChange(e);
                                    handleDateChange(e, 'startDate');
                                }}
                                value={value ? moment(value) : null}
                                id={`start-date${index}`}
                                label="Start date"
                                {...required}
                                minDate={minStartDate}
                                maxDate={maxStartDate}
                                disabled={
                                    isViewOnly ||
                                    isInvoiceReviewLocked ||
                                    requiresBillType ||
                                    (isPosted && isViewOnly) ||
                                    (isPendingSubmission && !enableEditByCorrection)
                                }
                                parentTextFieldProps={{
                                    error: Boolean(errors.expenseEntries?.[index]?.startDate),
                                    helperText: errors.expenseEntries?.[index]?.startDate?.message,
                                }}
                                parentSx={{
                                    '& .MuiInputBase-root': {
                                        borderColor: !errors.expenseEntries?.[index]?.startDate
                                            ? 'default'
                                            : `${COLORS.LT_PEPPER} !important`,
                                    },
                                }}
                            />
                        )}
                    />
                </Grid2>
                <Grid2 item size={{ xs: 12, md: 6 }}>
                    <Controller
                        control={control}
                        name={`expenseEntries[${index}].endDate`}
                        defaultValue={expense.endDate || null}
                        rules={
                            isPosted
                                ? {}
                                : {
                                      required: required.required
                                          ? (requiresBillType && 'Bill type is required') ||
                                            'End date required'
                                          : false,
                                      validate: {
                                          invalid: (value) =>
                                              !value || moment(value).isValid() || 'Invalid date',
                                          maxDate: (value) =>
                                              !value ||
                                              moment(value).isSameOrBefore(maxEndDate) ||
                                              'Max date reached',
                                          minDate: (value) =>
                                              !value ||
                                              moment(value).isSameOrAfter(minEndDate) ||
                                              'End date must be after start date',
                                      },
                                  }
                        }
                        render={({ onChange, value }) => (
                            <ExpenseDatePicker
                                onChange={(e) => {
                                    onChange(e);
                                    handleDateChange(e, 'endDate');
                                }}
                                value={value ? moment(value) : null}
                                id={`end-date${index}`}
                                label="End date"
                                {...required}
                                minDate={minEndDate}
                                maxDate={maxEndDate}
                                disabled={
                                    isViewOnly ||
                                    isInvoiceReviewLocked ||
                                    requiresBillType ||
                                    (isPosted && isViewOnly) ||
                                    (isPendingSubmission && !enableEditByCorrection)
                                }
                                parentTextFieldProps={{
                                    error: Boolean(errors.expenseEntries?.[index]?.endDate),
                                    helperText: errors.expenseEntries?.[index]?.endDate?.message,
                                }}
                                parentSx={{
                                    '& .MuiInputBase-root': {
                                        borderColor: !errors.expenseEntries?.[index]?.endDate
                                            ? 'default'
                                            : `${COLORS.LT_PEPPER} !important`,
                                    },
                                }}
                            />
                        )}
                    />
                </Grid2>
            </Grid2>
        </Grid2>
    );
};

export default ExpenseDateRange;
